(function (g: any) {
  'use strict';

  const VERSION = 1;
  const LOCAL_KEY = 'md:navigation';
  const SERVER_KEY = 'navigation';
  /* Private drag flavour for destinations that already belong to this account.
     The same gesture also carries the share URL for drops outside ManDana, but
     a Desk asks for this flavour first so it never mistakes owned data for an
     external bookmark that needs a new record. */
  const MD_NAVIGATION_DRAG_TYPE = 'application/x-mandana-navigation-target';
  const ID = /^[a-z0-9]{6,40}$/;
  const DOMAIN = /^e\d{1,3}$/;
  const DAY = /^\d{4}-\d{2}-\d{2}$/;
  const WINDOWS = new Set(['projects', 'databases', 'settings', 'boards', 'crafts', 'manifestations']);
  /* Deep-link token -> the screen Settings opens on. `city` is the screen's old
     name and stays here as an alias: a shared link is a promise, and renaming a
     screen does not release us from one somebody already wrote down. */
  const SETTINGS: Record<string, string> = {
    workflows: 'workflows',
    'calculation-method': 'method',
    place: 'place',
    city: 'place',
    trash: 'trash',
    data: 'data',
    profile: 'profile',
    devices: 'devices',
  };
  type Favorite = { add: number; remove: number; order: number; orderAt: number };
  type Navigation = { v: number; recents: Record<string, number>; favorites: Record<string, Favorite> };

  const clock = (v: unknown): number => typeof v === 'number' && Number.isFinite(v) && v >= 0 ? v : 0;
  const position = (v: unknown): number => typeof v === 'number' && Number.isFinite(v) && v >= 0 ? Math.floor(v) : 0;
  const empty = (): Navigation => ({ v: VERSION, recents: {}, favorites: {} });
  function validDay(value: unknown): boolean {
    if (typeof value !== 'string' || !DAY.test(value)) return false;
    const bits = value.split('-').map(Number);
    const y = bits[0] || 0, m = bits[1] || 0, d = bits[2] || 0;
    const date = new Date(Date.UTC(y, m - 1, d));
    return date.getUTCFullYear() === y && date.getUTCMonth() === m - 1 && date.getUTCDate() === d;
  }

  function mdNavigationPlace(target: unknown): any | null {
    if (typeof target !== 'string' || target.length > 180) return null;
    if (target.startsWith('record:')) {
      const key = target.slice('record:'.length);
      const parsed = typeof g.mdRecordRefParse === 'function' ? g.mdRecordRefParse(key) : null;
      const canonical = parsed && typeof g.mdRecordRefKey === 'function' ? g.mdRecordRefKey(parsed) : '';
      return canonical && (canonical === key || key.startsWith('manifest:'))
        ? { go: 'record', ref: canonical } : null;
    }
    const p = target.split(':');
    const one = p[1] || '', two = p[2] || '';
    if (p[0] === 'window' && p.length === 2 && WINDOWS.has(one)) {
      if (one === 'projects') return { go: 'workshop' };
      if (one === 'crafts' || one === 'manifestations') return { go: 'crafts' };
      return { go: one };
    }
    if (p[0] === 'setting' && p.length === 2 && SETTINGS[one])
      return { go: 'settings', view: SETTINGS[one] };
    if (p[0] === 'day' && p.length === 2 && validDay(one)) return { go: 'day', day: one };
    if (p[0] === 'project' && p.length === 2 && ID.test(one)) return { go: 'project', id: one };
    if (p[0] === 'pursuit' && p.length === 2 && ID.test(one)) return { go: 'pursuit', id: one };
    if (p[0] === 'domain' && p.length === 2 && DOMAIN.test(one)) return { go: 'domain', id: one };
    if (p[0] === 'database' && p.length === 2 && ID.test(one)) return { go: 'database', id: one };
    if (p[0] === 'database-row' && p.length === 3 && ID.test(one) && ID.test(two))
      return { go: 'database', id: one, row: two };
    if (p[0] === 'board' && p.length === 2 && ID.test(one)) return { go: 'board', id: one };
    if ((p[0] === 'craft' || p[0] === 'manifestation') && p.length === 2 && ID.test(one))
      return { go: 'crafts', id: one };
    if (p[0] === 'view' && p.length === 3 && one === 'account' && ID.test(two))
      return { go: 'view', scope: one, id: two };
    const three = p[3] || '';
    if (p[0] === 'view' && p.length === 4 && one === 'project' && ID.test(two) && ID.test(three))
      return { go: 'view', scope: one, projectId: two, id: three };
    if (p[0] === 'view' && p.length === 4 && one === 'database' && ID.test(two) && ID.test(three))
      return { go: 'view', scope: one, databaseId: two, id: three };
    return null;
  }

  function mdNavigationTarget(place: any, sourceKey?: unknown): string | null {
    if (!place || typeof place !== 'object') return null;
    if (place.go === 'record' && typeof place.ref === 'string') {
      const parsed = typeof g.mdRecordRefParse === 'function' ? g.mdRecordRefParse(place.ref) : null;
      const stable = parsed && typeof g.mdRecordRef === 'function' ? g.mdRecordRef(parsed.type, parsed.id) : null;
      const canonical = stable && typeof g.mdRecordRefKey === 'function' ? g.mdRecordRefKey(stable) : '';
      return canonical ? `record:${canonical}` : null;
    }
    if (typeof sourceKey === 'string') {
      const raw = sourceKey.startsWith('md:') ? sourceKey.slice(3) : sourceKey;
      const p = raw.split(':');
      const one = p[1] || '', two = p[2] || '';
      if ((p[0] === 'events' || p[0] === 'records') && p.length === 2 && validDay(one)) return `day:${one}`;
      if (p[0] === 'project' && p.length === 2 && ID.test(one)) return raw;
      if (p[0] === 'pursuit' && p.length === 2 && ID.test(one)) return raw;
      if (p[0] === 'domain' && p.length === 2 && DOMAIN.test(one)) return raw;
      if (p[0] === 'database' && p.length === 2 && ID.test(one)) return raw;
      if (p[0] === 'database-row' && p.length === 3 && ID.test(one) && ID.test(two)) return raw;
      if (p[0] === 'board' && p.length === 2 && ID.test(one)) return raw;
      if (p[0] === 'view' && p.length === 2 && ID.test(one) && place.scope === 'database'
        && ID.test(place.databaseId || '')) return `view:database:${place.databaseId}:${one}`;
    }
    if (place.go === 'workshop' || place.go === 'projects') return 'window:projects';
    if (place.go === 'settings') {
      const setting = Object.entries(SETTINGS).find(([, view]) => view === place.view);
      return setting ? `setting:${setting[0]}` : 'window:settings';
    }
    if (['databases', 'boards'].includes(place.go)) return `window:${place.go}`;
    if (place.go === 'crafts' || place.go === 'manifests')
      return place.id && ID.test(place.id) ? `craft:${place.id}` : 'window:crafts';
    if (place.go === 'day' && validDay(place.day || '')) return `day:${place.day}`;
    if (place.go === 'project' && ID.test(place.id || '')) return `project:${place.id}`;
    if (place.go === 'pursuit' && ID.test(place.id || '')) return `pursuit:${place.id}`;
    if (place.go === 'domain' && DOMAIN.test(place.id || '')) return `domain:${place.id}`;
    if (place.go === 'database' && ID.test(place.id || '')) return place.row && ID.test(place.row)
      ? `database-row:${place.id}:${place.row}` : `database:${place.id}`;
    if (place.go === 'board' && ID.test(place.id || '')) return `board:${place.id}`;
    if (place.go === 'view' && place.scope === 'account' && ID.test(place.id || '')) return `view:account:${place.id}`;
    if (place.go === 'view' && place.scope === 'project' && ID.test(place.projectId || '') && ID.test(place.id || ''))
      return `view:project:${place.projectId}:${place.id}`;
    if (place.go === 'view' && place.scope === 'database' && ID.test(place.databaseId || '') && ID.test(place.id || ''))
      return `view:database:${place.databaseId}:${place.id}`;
    return null;
  }

  function mdNavigationSanitize(value: unknown): Navigation {
    const out = empty();
    if (!value || typeof value !== 'object' || Array.isArray(value)) return out;
    const doc = value as any;
    if (doc.recents && typeof doc.recents === 'object' && !Array.isArray(doc.recents)) {
      for (const [target, at] of Object.entries(doc.recents)) {
        const stamp = clock(at), place = mdNavigationPlace(target);
        const canonical = place ? mdNavigationTarget(place) : null;
        if (stamp && canonical && !canonical.startsWith('window:'))
          out.recents[canonical] = Math.max(out.recents[canonical] || 0, stamp);
      }
    }
    if (doc.favorites && typeof doc.favorites === 'object' && !Array.isArray(doc.favorites)) {
      for (const [target, raw] of Object.entries(doc.favorites)) {
        const place = mdNavigationPlace(target);
        const canonical = place ? mdNavigationTarget(place) : null;
        if (!canonical || !raw || typeof raw !== 'object' || Array.isArray(raw)) continue;
        const f = raw as any;
        const clean = { add: clock(f.add), remove: clock(f.remove), order: position(f.order), orderAt: clock(f.orderAt) };
        if (clean.add || clean.remove || clean.orderAt) {
          const before = out.favorites[canonical];
          if (!before) out.favorites[canonical] = clean;
          else {
            const chosen = clean.orderAt > before.orderAt ? clean : before;
            out.favorites[canonical] = { add: Math.max(before.add, clean.add),
              remove: Math.max(before.remove, clean.remove), order: chosen.order,
              orderAt: Math.max(before.orderAt, clean.orderAt) };
          }
        }
      }
    }
    return out;
  }
  const copy = (doc: unknown): Navigation => mdNavigationSanitize(doc);

  function mdNavigationOpen(doc: unknown, target: unknown, at: unknown = Date.now()): Navigation {
    const out = copy(doc); const stamp = clock(at);
    const place = typeof target === 'string' ? mdNavigationPlace(target) : null;
    const canonical = place ? mdNavigationTarget(place) : null;
    if (canonical && stamp && !canonical.startsWith('window:'))
      out.recents[canonical] = Math.max(out.recents[canonical] || 0, stamp);
    return out;
  }
  function mdNavigationFavorite(doc: unknown, target: unknown, want: unknown, at: unknown = Date.now()): Navigation {
    const out = copy(doc); const stamp = clock(at);
    const place = typeof target === 'string' ? mdNavigationPlace(target) : null;
    const canonical = place ? mdNavigationTarget(place) : null;
    if (!canonical || !stamp) return out;
    const before = out.favorites[canonical] || { add: 0, remove: 0, order: Object.keys(out.favorites).length, orderAt: 0 };
    out.favorites[canonical] = { ...before, [want ? 'add' : 'remove']: stamp };
    return out;
  }
  function mdNavigationReorder(doc: unknown, targets: unknown, at: unknown = Date.now()): Navigation {
    const out = copy(doc); const stamp = clock(at);
    if (!Array.isArray(targets) || !stamp) return out;
    let atPosition = 0;
    for (const target of targets) {
      if (typeof target !== 'string') continue;
      const f = out.favorites[target];
      if (f && f.add > f.remove) out.favorites[target] = { ...f, order: atPosition++, orderAt: stamp };
    }
    return out;
  }
  function mdNavigationMerge(left: unknown, right: unknown): Navigation {
    const a = copy(left), b = copy(right), out = empty();
    for (const target of new Set([...Object.keys(a.recents), ...Object.keys(b.recents)]))
      out.recents[target] = Math.max(a.recents[target] || 0, b.recents[target] || 0);
    for (const target of new Set([...Object.keys(a.favorites), ...Object.keys(b.favorites)])) {
      const x = a.favorites[target] || { add: 0, remove: 0, order: 0, orderAt: 0 };
      const y = b.favorites[target] || { add: 0, remove: 0, order: 0, orderAt: 0 };
      const chosen = y.orderAt > x.orderAt || (y.orderAt === x.orderAt && y.order < x.order) ? y : x;
      out.favorites[target] = { add: Math.max(x.add, y.add), remove: Math.max(x.remove, y.remove),
        order: chosen.order, orderAt: Math.max(x.orderAt, y.orderAt) };
    }
    return out;
  }
  function mdNavigationRecents(doc: unknown, boundary = 0) {
    const clean = copy(doc), start = clock(boundary);
    return Object.entries(clean.recents).filter(([, at]) => at >= start)
      .map(([target, at]) => ({ target, at })).sort((a, b) => b.at - a.at || a.target.localeCompare(b.target));
  }
  function mdNavigationFavorites(doc: unknown) {
    const clean = copy(doc);
    return Object.entries(clean.favorites).filter(([, f]) => f.add > f.remove)
      .map(([target, f]) => ({ target, ...f }))
      .sort((a, b) => a.order - b.order || b.orderAt - a.orderAt || b.add - a.add || a.target.localeCompare(b.target));
  }
  function mdNavigationCompact(doc: unknown, boundary: unknown): Navigation {
    const out = copy(doc), start = clock(boundary);
    for (const [target, at] of Object.entries(out.recents)) if (at < start) delete out.recents[target];
    for (const [target, f] of Object.entries(out.favorites))
      if (f.remove >= f.add && f.remove < start && f.add < start) delete out.favorites[target];
    return out;
  }
  function mdNavigationCycleStart(now: unknown = Date.now(), fajrMinute: unknown = null): number {
    const stamp = clock(now) || Date.now(), date = new Date(stamp);
    const midnight = new Date(date.getFullYear(), date.getMonth(), date.getDate()).getTime();
    if (typeof fajrMinute !== 'number' || !Number.isFinite(fajrMinute) || fajrMinute < 0 || fajrMinute >= 1440)
      return midnight;
    const boundary = midnight + Math.floor(fajrMinute) * 60_000;
    return stamp < boundary ? boundary - 86_400_000 : boundary;
  }

  function mdNavigationRead(): Navigation { return copy(typeof g.mdRead === 'function' ? g.mdRead(LOCAL_KEY, null) : null); }
  function write(doc: Navigation): Navigation {
    const clean = copy(doc);
    if (typeof g.mdWrite === 'function') g.mdWrite(LOCAL_KEY, clean);
    if (typeof window !== 'undefined' && typeof window.dispatchEvent === 'function')
      window.dispatchEvent(new CustomEvent('mdnavigationchange', { detail: clean }));
    return clean;
  }
  const mdNavigationRecord = (target: unknown, at: unknown = Date.now(), boundary?: unknown) => {
    let start = typeof boundary === 'number' ? boundary : null;
    if (start === null) {
      const fajr = typeof g.mdPrayersFor === 'function'
        ? (g.mdPrayersFor(0) || []).find((p: any) => p && p.id === 'fajr') : null;
      start = mdNavigationCycleStart(Date.now(), fajr && fajr.min);
    }
    return write(mdNavigationCompact(mdNavigationOpen(mdNavigationRead(), target, at), start));
  };
  const mdNavigationSetFavorite = (target: unknown, want: unknown, at: unknown = Date.now()) =>
    write(mdNavigationFavorite(mdNavigationRead(), target, want, at));
  const mdNavigationSaveOrder = (targets: unknown, at: unknown = Date.now()) =>
    write(mdNavigationReorder(mdNavigationRead(), targets, at));
  function mdNavigationHref(target: unknown, base?: unknown): string {
    if (typeof target !== 'string') return '';
    const place = mdNavigationPlace(target); if (!place) return '';
    const canonical = mdNavigationTarget(place) || target;
    try {
      const fallback = typeof g.location?.href === 'string' ? g.location.href : 'https://mandana.local/';
      const url = new URL(typeof base === 'string' && base ? base : fallback);
      url.hash = `md=${encodeURIComponent(canonical)}`;
      return url.href;
    } catch (_) { return ''; }
  }
  function mdNavigationTargetFromUrl(value?: unknown): string | null {
    try {
      const fallback = typeof g.location?.href === 'string' ? g.location.href : 'https://mandana.local/';
      const url = new URL(typeof value === 'string' && value ? value : fallback);
      const match = url.hash.match(/^#md=(.*)$/);
      if (!match) return null;
      const target = decodeURIComponent(match[1] || '');
      const place = mdNavigationPlace(target);
      const canonical = place ? (mdNavigationTarget(place) || target) : null;
      if (canonical && canonical !== target && value === undefined && g.history?.replaceState) {
        const href = mdNavigationHref(canonical);
        if (href) g.history.replaceState({ mdTarget: canonical }, '', href);
      }
      return canonical;
    } catch (_) { return null; }
  }
  /* The surface side-channel that used to live here — MarkHistory,
     EnterSurface, HistorySurface, BackFrom — died at N-8. History entries are
     stamped and answered by the scene journal (md-scene.ts, wired in md-app);
     Tabs is an ordinary scene entry, and no close needs to guess its parent
     from a mark on the state. */
  /* Closing a routed destination must not leave its `#md=…` standing on an
     entry the close just abandoned. That hash is a resurrection: it survives
     the close, so the next reload, share, or stray popstate opens a window
     nobody asked for — the exact "previous project comes back" bug. When a
     close cannot walk the stack (no Tabs parent to hand back to, i.e. a
     direct or shared link) the honest end state is the bare URL. Rewriting in
     place keeps the current entry — its scroll, its surface mark — while
     dropping only the destination that no longer matches what is on screen. */
  function mdNavigationClearRoutedTarget(): boolean {
    const target = mdNavigationTargetFromUrl();
    if (!target || !g.history || typeof g.history.replaceState !== 'function') return false;
    try {
      const url = new URL(typeof g.location?.href === 'string' ? g.location.href : 'https://mandana.local/');
      url.hash = '';
      const state = g.history.state && typeof g.history.state === 'object' ? g.history.state : {};
      const rest = { ...state } as any;
      delete rest.mdTarget; delete rest.mdFrom;
      g.history.replaceState(rest, '', url.pathname + url.search + url.hash);
      return true;
    } catch (_) { return false; }
  }
  function mdNavigationNavigate(target: unknown, options?: unknown): boolean {
    if (typeof target !== 'string') return false;
    const place = mdNavigationPlace(target); if (!place) return false;
    const canonical = mdNavigationTarget(place); if (!canonical) return false;
    const opts = options && typeof options === 'object' ? options as any : {};
    if (opts.record !== false) mdNavigationRecord(canonical);
    const href = mdNavigationHref(canonical);
    if (opts.history !== false && href && g.history) {
      const method = opts.replace ? 'replaceState' : 'pushState';
      if (typeof g.history[method] === 'function') g.history[method]({ mdTarget: canonical,
        mdFrom: typeof opts.source === 'string' ? opts.source : null }, '', href);
    }
    if (typeof g.dispatchEvent === 'function')
      g.dispatchEvent(new CustomEvent('mdnavigationopen', {
        detail: { target: canonical, place, href, source: typeof opts.source === 'string' ? opts.source : null },
      }));
    return true;
  }
  const mdNavigationSyncDocs = () => ({ [SERVER_KEY]: mdNavigationRead() });
  function mdNavigationApplyDoc(key: unknown, value: unknown): boolean {
    if (key !== SERVER_KEY) return false;
    write(mdNavigationMerge(mdNavigationRead(), value)); return true;
  }
  const mdNavigationMergesDoc = (key: unknown) => key === SERVER_KEY;

  Object.assign(g, { MD_NAVIGATION_DRAG_TYPE, mdNavigationEmpty: empty, mdNavigationTarget, mdNavigationPlace, mdNavigationSanitize,
    mdNavigationOpen, mdNavigationFavorite, mdNavigationReorder, mdNavigationMerge, mdNavigationRecents,
    mdNavigationFavorites, mdNavigationCompact, mdNavigationCycleStart, mdNavigationRead,
    mdNavigationRecord, mdNavigationSetFavorite, mdNavigationSaveOrder, mdNavigationHref,
    mdNavigationTargetFromUrl, mdNavigationClearRoutedTarget,
    mdNavigationNavigate, mdNavigationSyncDocs,
    mdNavigationApplyDoc, mdNavigationMergesDoc });
})(typeof window !== 'undefined' ? window : globalThis);
